import type { ParticipantSnapshot, TrackSnapshot } from '@/entities/call'
import { cn } from '@/shared/lib'
import { VideoTile } from './video-tile'

interface VideoGridProps {
  participants: ParticipantSnapshot[]
  activeSpeakerIds: string[]
  // «Липкий» активный спикер для крупного плана — считает callStore
  // (не пропадает после окончания речи, передача другому — через грейс).
  spotlightId?: string | null
  // MRU-история спикеров (последний — первым) — порядок ленты миниатюр;
  // не говорившие идут после, в порядке появления.
  speakerRank?: string[]
}

interface CamTile {
  key: string
  name: string
  isLocal: boolean
  track: TrackSnapshot | null
  audioMuted: boolean
  active: boolean
}

interface ScreenTile {
  key: string
  name: string
  isLocal: boolean
  track: TrackSnapshot
}

// Лента миниатюр (вебки над крупным планом) — фикс. высота, горизонт. скролл;
// вертикально не растёт, чтобы не выдавливать крупный план за вьюпорт.
const FILMSTRIP = 'flex shrink-0 gap-2 overflow-x-auto sm:gap-3'
const FILMSTRIP_ITEM = 'aspect-video h-24 shrink-0 md:h-28'

// Лента миниатюр ранжируется по свежести речи: говорившие — по MRU-рангу,
// остальные — следом в порядке появления. Сетка ≤4 без демки не переставляется.
function byRank(tiles: CamTile[], rank: string[]): CamTile[] {
  if (rank.length === 0) return tiles
  const pos = (t: CamTile) => {
    const i = rank.indexOf(t.key.replace(/:cam$/, ''))
    return i === -1 ? rank.length : i
  }
  return tiles
    .map((tile, index) => ({ tile, index }))
    .sort((a, b) => pos(a.tile) - pos(b.tile) || a.index - b.index)
    .map((x) => x.tile)
}

export function VideoGrid({
  participants,
  activeSpeakerIds,
  spotlightId,
  speakerRank = [],
}: VideoGridProps) {
  if (participants.length === 0) {
    return (
      <div className="flex h-full items-center justify-center text-text-tertiary">
        Ожидание участников…
      </div>
    )
  }

  // Разворачиваем участников в тайлы: у каждого — тайл камеры (или аватар),
  // плюс отдельный тайл на каждую демонстрацию экрана.
  // Замьюченная камера → track=null → тайл рисует заглушку с инициалами.
  const camTiles: CamTile[] = participants.map((p) => {
    const cam = p.tracks.find((t) => t.kind === 'video' && t.source === 'camera')
    return {
      key: `${p.id}:cam`,
      name: p.name,
      isLocal: p.isLocal,
      track: cam && !cam.muted ? cam : null,
      audioMuted: p.tracks.some((t) => t.kind === 'audio' && t.muted),
      active: activeSpeakerIds.includes(p.id),
    }
  })

  const screenTiles: ScreenTile[] = participants.flatMap((p) =>
    p.tracks
      .filter((t) => t.kind === 'video' && t.source === 'screen_share' && !t.muted)
      .map((t) => ({ key: t.id, name: p.name, isLocal: p.isLocal, track: t })),
  )

  // Режим демонстрации: шара крупным планом, все вебки — лентой сверху.
  if (screenTiles.length > 0) {
    return (
      <div className="flex h-full flex-col gap-3">
        <div className={FILMSTRIP}>
          {byRank(camTiles, speakerRank).map((t) => (
            <div key={t.key} className={FILMSTRIP_ITEM}>
              <VideoTile
                name={t.name}
                track={t.track}
                isLocal={t.isLocal}
                active={t.active}
                audioMuted={t.audioMuted}
              />
            </div>
          ))}
        </div>
        <div
          className={cn(
            'grid min-h-0 flex-1 gap-3',
            screenTiles.length > 1 ? 'grid-cols-2' : 'grid-cols-1',
          )}
        >
          {screenTiles.map((t) => (
            <VideoTile
              key={t.key}
              name={t.name}
              track={t.track}
              isLocal={t.isLocal}
              active={false}
              audioMuted={false}
              screen
            />
          ))}
        </div>
      </div>
    )
  }

  // Режим «говорящий»: ≥5 камер — активный спикер крупно снизу, лента сверху.
  // Крупный план — липкий spotlightId; фолбэки — на текущего говорящего
  // и на первого участника в самом старте звонка, когда никто не говорил.
  if (camTiles.length > 4) {
    const spotlight =
      (spotlightId != null ? camTiles.find((t) => t.key === `${spotlightId}:cam`) : undefined) ??
      camTiles.find((t) => t.active) ??
      camTiles[0]
    if (!spotlight) return null
    const rest = byRank(
      camTiles.filter((t) => t.key !== spotlight.key),
      speakerRank,
    )
    return (
      <div className="flex h-full flex-col gap-3">
        <div className={FILMSTRIP}>
          {rest.map((t) => (
            <div key={t.key} className={FILMSTRIP_ITEM}>
              <VideoTile
                name={t.name}
                track={t.track}
                isLocal={t.isLocal}
                active={t.active}
                audioMuted={t.audioMuted}
              />
            </div>
          ))}
        </div>
        <section aria-label="Крупный план" className="min-h-0 flex-1">
          <VideoTile
            name={spotlight.name}
            track={spotlight.track}
            isLocal={spotlight.isLocal}
            active={spotlight.active}
            audioMuted={spotlight.audioMuted}
          />
        </section>
      </div>
    )
  }

  // Режим сетки: ≤4 камеры — равные плитки. Десктоп: 1 — во весь экран,
  // 2 — рядом, 3-4 — 2×2. Портретная мобила: двое — колонкой (тайлы шире),
  // 3-4 — та же 2×2.
  const cols =
    camTiles.length === 1
      ? 'grid-cols-1'
      : camTiles.length === 2
        ? 'grid-cols-1 sm:grid-cols-2'
        : 'grid-cols-2'
  const rows =
    camTiles.length === 1
      ? 'grid-rows-1'
      : camTiles.length === 2
        ? 'grid-rows-2 sm:grid-rows-1'
        : 'grid-rows-2'
  return (
    <div className={cn('grid h-full w-full gap-2 sm:gap-3', cols, rows)}>
      {camTiles.map((t) => (
        <VideoTile
          key={t.key}
          name={t.name}
          track={t.track}
          isLocal={t.isLocal}
          active={t.active}
          audioMuted={t.audioMuted}
        />
      ))}
    </div>
  )
}
